import FetchModelAssignments from '@Application/Ai/AiConfiguration/Assignments/Queries/FetchModelAssignments';
import fetchModelAssignments from '@Application/Ai/AiConfiguration/Assignments/fetchModelAssignments';
import FetchAiModels from '@Application/Ai/AiConfiguration/Models/Queries/FetchAiModels';
import fetchAiModels from '@Application/Ai/AiConfiguration/Models/fetchAiModels';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import UsageAssignmentsBoard from '@UI/Pages/AiAssignments/Shared/UsageAssignmentsBoard';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import { css } from '@emotion/react';
import { TriangleAlert } from 'lucide-react';
import type * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const catalogSkeletonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

const cardsSkeletonGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(18rem, 100%), 1fr));
	gap: 1.25rem;
`;

/**
 * Acts on the current organization — the header selector's own choice, never a list to enter. Owns
 * the model catalogue query (account-scoped, always needed to resolve the cards) and the organization
 * assignment slot.
 */
const OrganizationAiAssignmentsContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;

	const {
		data: models,
		error: modelsError,
		refetch: refetchModels,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.models,
		stopOn: (value) => !!value && value.scopeId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAiModels(new FetchAiModels(accountId)));
		},
		deps: [accountId],
	});

	const {
		data: assignments,
		error: assignmentsError,
		refetch: refetchAssignments,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.organizationAssignments,
		stopOn: (value) => !!value && value.scopeType === 'organization' && value.scopeId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchModelAssignments(new FetchModelAssignments({ type: 'organization', organizationId })),
			);
		},
		deps: [organizationId],
	});

	if (organizationId === null) {
		return (
			<EmptyState
				title="Aucune structure"
				description="Vous n’êtes affecté à aucune structure : aucune affectation ne peut être chargée."
				icon={<TriangleAlert size={28} />}
			/>
		);
	}

	if (models === null && modelsError === null) {
		return (
			<div css={catalogSkeletonStyle}>
				<Skeleton height="16rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (models === null) {
		return (
			<ErrorState
				title={'Impossible de charger les modèles de la structure.'}
				onRetry={refetchModels}
			/>
		);
	}

	if (assignments === null && assignmentsError === null) {
		return (
			<div css={cardsSkeletonGridStyle}>
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (assignments === null) {
		return (
			<ErrorState
				title={'Impossible de charger les modèles de la structure.'}
				onRetry={refetchAssignments}
			/>
		);
	}

	return (
		<UsageAssignmentsBoard
			scope={{ type: 'organization', organizationId }}
			assignments={assignments}
			models={models}
		/>
	);
};

export default OrganizationAiAssignmentsContent;
